import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useState, useEffect, useRef } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import banner from '@/assets/Logos/banner.svg';
import StoreBadgeLink from '@/components/StoreBadgeLink';
import SuccessModal from '@/components/SuccessModal/SuccessModal';
import { color } from '../../themes/color';
import { typography } from '../../themes/typography';
import CustomButton from '../Button/button';
import instagram from '@/assets/Footer/instagram.svg';
import facebook from '@/assets/Footer/facebook.svg';
import linkedin from '@/assets/Footer/linkedin.svg';
import {
  FOOTER_APP_STORES_ID,
  FOOTER_SUBSCRIBE_ID,
} from '@/constants/footerAnchors';
import { subscribe } from '@/services/api/authService';

const CONTACT_EMAIL = 'hello@ukvineyardguide.co.uk';
const CONTACT_SUBJECT = 'Partnership Inquiry - UK Vineyard Guide';
const CONTACT_BODY =
  "Hi,\n\nI'm interested in working with UK Vineyard Guide.\n\n";

export interface FooterLink {
  label: string;
  href?: string;
}

export interface FooterProps {
  column1?: readonly FooterLink[];
  column2?: readonly FooterLink[];
  className?: string;
  isFaqPage?: boolean;
}

const DEFAULT_COLUMN_1 = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Partners', href: '#partners' },
] as const;

const DEFAULT_COLUMN_2 = [
  {
    label: 'Privacy Policy',
    href: '/UK%20Vineyard%20-%20Privacy%20Policy.pdf',
  },
  {
    label: 'Terms of Service',
    href: '/UK%20Vineyard%20-%20Terms%20of%20Use.pdf',
  },
  {
    label: 'FAQ',
    href: '/faq',
  },
] as const;

const linkSx = {
  color: color.cream['100'],
  fontWeight: 300,
  fontFamily: {
    xs: typography.caption.fontFamily,
    md: typography.bodyText.fontFamily,
  },
  textDecoration: 'none',
  fontSize: {
    md: typography.bodyText.fontSize,
  },
  lineHeight: '1.2',
  display: 'block',
  '&:hover': { color: color.moss.default },
};

const columnSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: { xs: '16px', md: '32px' },
};

export default function Footer({
  column1 = DEFAULT_COLUMN_1,
  column2 = DEFAULT_COLUMN_2,
  className,
  isFaqPage = false,
}: FooterProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (window.location.hash !== `#${FOOTER_SUBSCRIBE_ID}`) return;

    const doScroll = () => {
      emailInputRef.current?.scrollIntoView({ block: 'center' });
      emailInputRef.current?.focus({ preventScroll: true });
    };

    if (document.readyState === 'complete') {
      doScroll();
    } else {
      window.addEventListener('load', doScroll, { once: true });
      return () => window.removeEventListener('load', doScroll);
    }
  }, []);

  // Hide "How It Works" and "Partners" on FAQ page since they're landing page anchors
  const displayColumn1 = isFaqPage
    ? column1.filter(
        (link) => !['How It Works', 'Partners'].includes(link.label)
      )
    : column1;
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<'success' | 'error'>(
    'success'
  );
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleContactClick = (): void => {
    gtag('event', 'cta_click', {
      event_category: 'Engagement',
      event_label: 'Contact Email – Footer',
    });
    const mailtoLink = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(CONTACT_SUBJECT)}&body=${encodeURIComponent(CONTACT_BODY)}`;
    window.location.href = mailtoLink;
  };

  const handleCopyEmail = (): void => {
    navigator.clipboard
      .writeText(CONTACT_EMAIL)
      .then(() => {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      })
      .catch(() => {
        // Fallback: select text from a temporary input element
        const input = document.createElement('input');
        input.value = CONTACT_EMAIL;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      });
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      await subscribe(email.trim());
      gtag('event', 'newsletter_subscribe', {
        event_category: 'Engagement',
        event_label: 'Newsletter Subscribe – Footer',
      });
      setShowSuccessModal(true);
      setEmail('');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to subscribe. Please try again later.';
      setSnackbarMessage(errorMessage);
      setSnackbarType('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      component="footer"
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        padding: { xs: '48px 24px 32px', md: '52px 48px 38px' },
        backgroundImage: `url(${banner})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '24px',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '24px',
            alignItems: 'flex-start',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            flex: { xs: '1 1 100%', md: '1' },
          }}
        >
          <Box
            id={FOOTER_SUBSCRIBE_ID}
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: '420px' },
              borderRadius: '10px',
              backgroundColor: color.moss.default,
              p: '16px',
            }}
          >
            <Typography
              sx={{
                ...typography.heading4,
                fontWeight: 350,
                color: color.cream['100'],
                mb: '16px',
              }}
            >
              Subscribe to our newsletter
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'stretch' },
                gap: '10px',
                height: { xs: 'auto', md: '51px' },
              }}
            >
              <Box
                component="form"
                onSubmit={handleSubscribe}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: '10px',
                  height: { xs: 'auto', md: '51px' },
                  width: '100%',
                }}
              >
                <Box
                  component="input"
                  ref={emailInputRef}
                  type="email"
                  placeholder="Email address"
                  aria-label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  sx={{
                    flex: { md: 1 },
                    width: '100%',
                    minWidth: 0,
                    minHeight: { xs: '48px', md: 'auto' },
                    boxSizing: 'border-box',
                    fontSize: typography.bodyText.fontSize,
                    borderRadius: '4px',
                    border: `1px solid ${color.cream['200']}`,
                    px: '12px',
                    ...typography.bodySmall,
                    color: color.cream['100'],
                    backgroundColor: 'transparent',
                    outline: 'none',
                    boxShadow: 'none !important',
                    '&::placeholder': { color: color.cream['200'], opacity: 1 },
                    '&:focus': {
                      outline: 'none',
                      border: `1px solid ${color.terracotta['500']}`,
                      boxShadow: 'none !important',
                    },
                    '&:disabled': {
                      opacity: 0.7,
                    },
                  }}
                />
                <CustomButton
                  type="submit"
                  variant="contained"
                  sx={{
                    flexShrink: 0,
                    width: { xs: '100%', md: 'auto' },
                  }}
                  disabled={isSubmitting || !email.trim()}
                  dataTrack="newsletter-subscribe"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </CustomButton>
              </Box>
            </Box>
          </Box>

          {/* QR Code - Desktop only next to subscribe */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },

              flexShrink: 0,
            }}
          >
            <StoreBadgeLink />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'center', md: 'flex-start' },

            gap: { xs: '16px', md: '32px' },
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'flex-start' },
              justifyContent: { xs: 'center', md: 'space-between' },
              gap: { xs: '16px', md: '32px' },
              flexWrap: { xs: 'nowrap', md: 'wrap' },
              minWidth: 0,
              borderLeft: {
                xs: '1px solid rgba(249, 248, 246, 0.45)',
                md: 'none',
              },
              px: { xs: '24px', md: 0 },
            }}
          >
            <Box sx={columnSx}>
              {displayColumn1.map((link) => (
                <Link key={link.label} href={link.href ?? '#'} sx={linkSx}>
                  {link.label}
                </Link>
              ))}
            </Box>
            <Box sx={columnSx}>
              {column2.map((link) => (
                <Link
                  key={link.label}
                  href={link.href ?? '#'}
                  target={link.href?.endsWith('.pdf') ? '_blank' : undefined}
                  rel={
                    link.href?.endsWith('.pdf')
                      ? 'noopener noreferrer'
                      : undefined
                  }
                  sx={linkSx}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              borderLeft: `1px solid rgba(249, 248, 246, 0.45)`,
              pl: '24px',
              minHeight: '104px',
            }}
          >
            <Typography
              sx={{
                ...typography.heading5,
                color: color.cream['100'],
                fontWeight: 350,
                mb: '16px',
                maxWidth: '210px',
              }}
            >
              Want to know more or get in touch?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                type="button"
                onClick={handleContactClick}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  color: color.cream['100'],
                  fontSize: typography.bodyText.fontSize,
                  fontFamily: typography.bodyText.fontFamily,
                  fontWeight: 300,
                  textDecoration: 'none',
                  textAlign: 'left',
                }}
              >
                {CONTACT_EMAIL}
              </button>
            </Box>
            <Box sx={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <Link
                href="https://www.instagram.com/ukvineyardguide/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={instagram} alt="Instagram" />
              </Link>
              <Link
                href="https://www.facebook.com/profile.php?id=61564798714929 "
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={facebook} alt="Facebook" />
              </Link>
              <Link
                href="https://www.linkedin.com/company/uk-vineyard-guide"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={linkedin} alt="LinkedIn" />
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        id={FOOTER_APP_STORES_ID}
        sx={{
          display: { xs: 'flex', sm: 'none' },
          gap: '12px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {/* Buttons - Mobile only */}
        <StoreBadgeLink
          platform="apple"
          onClick={(e) => {
            e.stopPropagation();
            gtag('event', 'store_badge_click', {
              event_category: 'Download',
              event_label: 'App Store – Footer',
            });
          }}
        />
        <StoreBadgeLink
          platform="android"
          onClick={(e) => {
            e.stopPropagation();
            gtag('event', 'store_badge_click', {
              event_category: 'Download',
              event_label: 'Google Play – Footer',
            });
          }}
        />
      </Box>

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Successfully Subscribed"
        message="Thank you for subscribing to our newsletter. Check your email for updates on the latest vineyards and events."
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarType}
          sx={{
            width: '100%',
            backgroundColor:
              snackbarType === 'success'
                ? color.olive.default
                : color.merlot.default,
            color: color.cream['100'],
            '& .MuiAlert-icon': {
              color: color.cream['100'],
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Fallback snackbar when no email client is detected */}
      <Snackbar
        open={showEmailFallback}
        autoHideDuration={6000}
        onClose={() => setShowEmailFallback(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setShowEmailFallback(false)}
          icon={false}
          sx={{
            width: '100%',
            backgroundColor: color.terracotta.default,
            color: 'white',
          }}
        >
          No email app configured. Copy this address:{' '}
          <button
            type="button"
            onClick={handleCopyEmail}
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              margin: 0,
              cursor: 'pointer',
              color: 'white',
              textDecoration: 'underline',
              fontSize: 'inherit',
              font: 'inherit',
            }}
          >
            <strong>{CONTACT_EMAIL}</strong>
          </button>
        </Alert>
      </Snackbar>

      {/* Snackbar for email copied confirmation */}
      <Snackbar
        open={copiedEmail}
        autoHideDuration={2000}
        onClose={() => setCopiedEmail(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setCopiedEmail(false)}
          sx={{
            width: '100%',
            backgroundColor: color.terracotta.default,
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            },
          }}
        >
          Email copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
}
